import React, { useEffect, useState } from "react";
import {
    Shield,
    Upload,
    Check,
    X,
    AlertCircle,
    FileText,
    Camera,
    Mail,
    Building,
    MapPin,
    Info,
    ChevronRight,
    InfoIcon,
    Phone,
    User,
} from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Spinner from "../../../shared/components/Spinner";
import {
    createUserVerificationData,
    currentUserVerificationData,
    updateUserVerification,
    useAppDispatch,
    sendPhoneVerificationCode,
    verifyPhoneOTP,
    UpdateUserProfile,
    getUserCompanies,
} from "../../../../redux";
import { useSelector } from "react-redux";
import { RoleEnum, VerificationStatusEnum } from "../../../shared/model";
import { selectLoaderValue } from "../../../../redux/Slices/loadingSlice";
import Card from "../../../../components/ui/card";
import Button from "../../../../components/ui/Button";
import Alert, { AlertDescription } from "../../../../components/ui/alert";
import SharedHeader5 from "../../../../Headers/SharedHeader5";
import { LogoutManually, LogoutUser } from "../../../../redux";
import SharedHeader4 from "../../../../Headers/SharedHeader4";
import SharedFooter3 from "../../../../Footer/SharedFooter3";
import { GlobalService } from "../../../shared";
import VerificationService from "../../../../services/verificationService";
import { uploadContractFile } from "../../../../redux/Slices/contractSlice";
import { useTranslation } from "react-i18next";

const VerificationStep = ({
    icon: Icon,
    title,
    description,
    status = "pending",
    isUploading = false,
    onUpload,
    inputId,
    onFileSelect,
    documents = [],
    uploadedFileName,
    isUploaded,
    isIdentity = false,
    isPhone = false,
    phoneNumber = "",
    onPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => { },
    onSendVerificationCode = () => { },
    otp = "",
    onOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => { },
    onVerifyOtp = () => { },
    isOtpSent = false,
    isOtpVerifying = false,
    user = null,
    notes = [],
    verificationType = "",
    hasVerificationData = false,
    additionalDocuments = [],
    onAdditionalFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => { },
}) => {
    const { t } = useTranslation();
    return (
        <Card color={undefined} className="p-6">
            <div className="flex items-start gap-4">
                <div
                    className={`p-2 rounded-lg ${status === "verified"
                        ? "bg-green-50"
                        : status === "pending"
                            ? "bg-blue-50"
                            : status === "rejected"
                                ? "bg-red-50"
                                : "bg-gray-50"
                        }`}>
                    <Icon
                        className={`h-6 w-6 ${status === "verified"
                            ? "text-green-500"
                            : status === "pending"
                                ? "text-blue-500"
                                : status === "rejected"
                                    ? "text-red-500"
                                    : "text-gray-500"
                            }`}
                    />
                </div>

                <div className="flex-1 space-y-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <h3 className="font-medium text-gray-900">{title}</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {description}
                                {isIdentity && (
                                    <span className="block mt-1 text-xs text-gray-400">
                                        {documents.length === 0
                                            ? t("verifyPage.uploadAtLeast2")
                                            : documents.length === 1
                                                ? t("verifyPage.upload1More")
                                                : ""}
                                    </span>
                                )}
                            </p>
                        </div>
                        {status === "verified" && (
                            <span className="flex items-center text-sm font-medium text-green-600">
                                <Check className="h-4 w-4 mr-1" />
                                {t("verifyPage.verified")}
                            </span>
                        )}
                        {status === "rejected" && (
                            <span className="flex items-center text-sm font-medium text-red-600">
                                <X className="h-4 w-4 mr-1" />
                                {t("verifyPage.rejected")}
                            </span>
                        )}
                        {documents.length > 0 && status === "pending" && (
                            <span className="flex items-center text-sm font-medium text-blue-500">
                                <InfoIcon className="h-4 w-4 mr-1" />
                                {t("verifyPage.pending")}
                            </span>
                        )}
                    </div>

                    {documents.length > 0 && (
                        <div className="space-y-2">
                            {documents.map((doc, index) => (
                                <div key={index} className="flex items-center p-2 bg-gray-50 rounded-lg">
                                    <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600 flex-1 break-all">{doc.name}</span>
                                    {status === "verified" ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <Upload className="h-4 w-4 text-blue-600" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {uploadedFileName?.length > 0 &&
                        uploadedFileName.map((name, i) => (
                            <div key={i} className="flex items-center p-2 bg-blue-50 rounded-lg">
                                <FileText className="h-5 w-5 text-blue-400 mr-2" />
                                <span className="text-sm text-blue-700 flex-1 break-all">{name}</span>
                                <Upload className="h-4 w-4 text-blue-600" />
                            </div>
                        ))}

                    {/* Display Additional Documents */}
                    {additionalDocuments && additionalDocuments.length > 0 && (
                        <div className="space-y-2 mt-3">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="h-px bg-blue-200 flex-1"></div>
                                <span className="text-xs font-medium text-blue-600">{t("verifyPage.additionalDocuments")}</span>
                                <div className="h-px bg-blue-200 flex-1"></div>
                            </div>
                            {additionalDocuments.map((doc, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center flex-1">
                                        <FileText className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
                                        <span className="text-sm text-blue-700 flex-1 break-all mr-2">{doc.path}</span>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                                        doc.status === "approved" ? "bg-green-100 text-green-700" :
                                        doc.status === "rejected" ? "bg-red-100 text-red-700" :
                                        "bg-yellow-100 text-yellow-700"
                                    }`}>
                                        {doc.status === "approved" ? t("verifyPage.approved") :
                                         doc.status === "rejected" ? t("verifyPage.rejected") :
                                         t("verifyPage.pending")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Admin Notes Display */}
                    {notes && notes.length > 0 && (() => {
                        // Filter notes based on status
                        const displayNotes = status === "verified" 
                            ? notes.filter(note => note.includes("Require Additional Documents"))
                            : notes;
                        
                        if (displayNotes.length === 0) return null;
                        
                        return (
                            <div className="mt-4 space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">{t("verifyPage.adminNotes")}</h4>
                                {displayNotes.map((note, index) => (
                                    <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                            <p className="text-sm text-amber-800">{note}</p>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Show upload option if "Require Additional Documents" note exists */}
                                {notes.some(note => note.includes("Require Additional Documents")) && (
                                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="flex items-start gap-2 mb-3">
                                            <Upload className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <h5 className="text-sm font-medium text-blue-800">{t("verifyPage.additionalDocsRequired")}</h5>
                                                <p className="text-sm text-blue-700">{t("verifyPage.uploadRequestedDocs")}</p>
                                            </div>
                                        </div>
                                        
                                        {/* Show uploaded additional documents */}
                                        {additionalDocuments && additionalDocuments.length > 0 && (
                                            <div className="mb-3 space-y-2">
                                                {additionalDocuments.map((doc, index) => (
                                                    <div key={index} className="flex items-center p-2 bg-white rounded-lg border border-blue-200">
                                                        <FileText className="h-5 w-5 text-blue-400 mr-2" />
                                                        <span className="text-sm text-blue-700 flex-1 break-all">{doc.path}</span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        doc.status === "approved" ? "bg-green-100 text-green-700" :
                                                        doc.status === "rejected" ? "bg-red-100 text-red-700" :
                                                        "bg-yellow-100 text-yellow-700"
                                                    }`}>
                                                        {doc.status === "approved" ? t("verifyPage.approved") :
                                                         doc.status === "rejected" ? t("verifyPage.rejected") :
                                                         t("verifyPage.pending")}
                                                    </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => document.getElementById(`additional-${inputId}`)?.click()}
                                                disabled={isUploading}
                                                size="sm"
                                                className="inline-flex items-center text-sm">
                                                {isUploading ? (
                                                    <>
                                                        <Spinner size={4} />
                                                        {t("verifyPage.uploading")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {t("verifyPage.uploadAdditionalDocuments")}
                                                    </>
                                                )}
                                            </Button>
                                            <input
                                                id={`additional-${inputId}`}
                                                type="file"
                                                className="hidden"
                                                accept="*/*"
                                                multiple
                                                onChange={onAdditionalFileSelect}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Phone Number Input Field */}
                    {isPhone && status !== "verified" && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t("verifyPage.phoneNumber")}
                                    {status === "verified" && (
                                        <span className="ml-2 text-sm font-normal text-green-600">
                                            {t("verifyPage.phoneVerified")}
                                        </span>
                                    )}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-black-500 text-sm">+</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={phoneNumber.replace(/^\+/, "")}
                                        onChange={onPhoneNumberChange}
                                        placeholder="1 (555) 123-4567"
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* OTP Input Field - Show after SMS is sent */}
                            {isOtpSent && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t("verifyPage.verificationCode")}
                                    </label>
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={onOtpChange}
                                        placeholder={t("verifyPage.codePlaceholder")}
                                        maxLength={6}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Show verified phone number when completed */}
                    {isPhone && status === "verified" && user?.phone && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2">
                                <Check className="h-5 w-5 text-green-600" />
                                <div>
                                    <p className="text-sm font-medium text-green-800">
                                        {t("verifyPage.phoneNumberVerified")}
                                    </p>
                                    <p className="text-sm text-green-700">
                                        {user.phone}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {status !== "verified" && (
                        <div className="flex flex-wrap gap-2">
                            {isPhone ? (
                                <>
                                    {!isOtpSent ? (
                                        <Button
                                            onClick={onSendVerificationCode}
                                            disabled={!phoneNumber.trim() || isUploading}
                                            className="inline-flex items-center text-sm">
                                            {isUploading ? (
                                                <>
                                                    <Spinner size={4} />
                                                    {t("verifyPage.sending")}
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    {t("verifyPage.sendVerificationCode")}
                                                </>
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={onVerifyOtp}
                                            disabled={!otp.trim() || otp.length !== 6 || isOtpVerifying}
                                            className="inline-flex items-center text-sm">
                                            {isOtpVerifying ? (
                                                <>
                                                    <Spinner size={4} />
                                                    {t("verifyPage.verifying")}
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="h-4 w-4 mr-2" />
                                                    {t("verifyPage.verifyCode")}
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </>
                            ) : (!notes?.some(note => note.includes("Require Additional Documents")) && (!hasVerificationData || !status || status === "pending" || status === "rejected")) ? (
                                <>
                                    <Button
                                        onClick={() => {
                                            // Bypass identity validation if additional documents are required
                                            const requiresAdditionalDocs = notes?.some(note => note.includes("Require Additional Documents"));
                                            
                                            if (isIdentity && documents.length >= 2 && uploadedFileName?.length === 0 && !requiresAdditionalDocs) {
                                                GlobalService.showToast(
                                                    "info",
                                                    "Info",
                                                    "You've already uploaded the required identity documents"
                                                );
                                                return;
                                            }
                                            onUpload();
                                        }}
                                        disabled={
                                            isUploading || (isIdentity && documents.length >= 2 && !uploadedFileName?.length && !notes?.some(note => note.includes("Require Additional Documents")))
                                        }
                                        className="inline-flex items-center text-sm">
                                        {isUploading ? (
                                            <>
                                                <Spinner size={4} />
                                                {t("verifyPage.uploading")}
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                {isUploaded ? t("verifyPage.documentUploaded") : t("verifyPage.uploadDocument")}
                                            </>
                                        )}
                                    </Button>

                                    <input
                                        id={inputId}
                                        type="file"
                                        className="hidden"
                                        accept="*/*"
                                        multiple
                                        onChange={onFileSelect}
                                    />
                                </>
                            ) : null}

                            {!isPhone && status === "rejected" && (
                                <Button
                                    variant="outline"
                                    className="inline-flex items-center text-sm text-red-600 border-red-200 hover:bg-red-50">
                                    <Info className="h-4 w-4 mr-2" />
                                    {t("verifyPage.viewIssue")}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

const VerifyAccount = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [uploadingSection, setUploadingSection] = useState<{
        [key in "identity" | "business" | "addressProof" | "phone"]?: boolean;
    }>({});
    const [isNewVerificationSubmitted, setIsNewVerificationSubmitted] = useState(false);

    const { user } = useSelector((state: any) => state.auth);
    const { company } = useSelector((state: any) => state.client);
    const isProfessional =
        Number(user?.role?.id) === RoleEnum.professional ||
        Number(user?.role?.id) === RoleEnum.internationl_professional;
    const rawVerificationData = useSelector((state: any) => state.client.verificationData);
    const verificationData =
        Array.isArray(rawVerificationData) && rawVerificationData.length > 0
            ? rawVerificationData[0]
            : rawVerificationData ?? null;

    const isLoading = useSelector(selectLoaderValue);

    const [uploadedFiles, setUploadedFiles] = useState({
        identity: [] as string[],
        business: [] as string[],
        addressProof: [] as string[],
    });

    const [uploadedFileNames, setUploadedFileNames] = useState({
        identity: [] as string[],
        business: [] as string[],
        addressProof: [] as string[],
    });

    // State for additional documents
    const [uploadedAdditionalFiles, setUploadedAdditionalFiles] = useState({
        identity: [] as Array<{ id: string; path: string; status: string }>,
        business: [] as Array<{ id: string; path: string; status: string }>,
        addressProof: [] as Array<{ id: string; path: string; status: string }>,
    });

    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isOtpVerifying, setIsOtpVerifying] = useState(false);

    const calculateVerificationProgress = () => {
        console.log(VerificationService.calculateVerificationProgress(user, verificationData, company));
        return VerificationService.calculateVerificationProgress(user, verificationData, company);
    };
    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
            navigate("/auth/login");
        } catch (error) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        }
    };

    useEffect(() => {
        if (user?.id) {
            dispatch(currentUserVerificationData({ id: user.id }));
            dispatch(getUserCompanies({ id: user.id }));
        }
        // Initialize phone number from user data if available
        if (user?.phone) {
            setPhoneNumber(user.phone);
        }
    }, [dispatch, user?.id, user?.phone]);

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, ""); // Remove all non-digits
        if (value) {
            value = "+" + value; // Add + prefix
        }
        setPhoneNumber(value);
    };

    const handleSendVerificationCode = async () => {
        if (!phoneNumber.trim()) {
            GlobalService.showToast("error", t("toast.titles.error"), t("verifyPage.enterValidPhone"));
            return;
        }

        setUploadingSection(prev => ({ ...prev, phone: true }));

        try {
            await dispatch(sendPhoneVerificationCode({
                phone: phoneNumber.trim(),
                id: user.id
            })).unwrap();

            setIsOtpSent(true);
            GlobalService.showToast("success", t("toast.titles.success"), t("verifyPage.codeSentSuccess"));
        } catch (error) {
            console.error("Failed to send verification code:", error);
        } finally {
            setUploadingSection(prev => ({ ...prev, phone: false }));
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp.trim() || otp.length !== 6) {
            GlobalService.showToast("error", t("toast.titles.error"), t("verifyPage.enterValidCode"));
            return;
        }

        setIsOtpVerifying(true);

        try {
            await dispatch(verifyPhoneOTP({
                otp: otp.trim(),
                id: user.id
            })).unwrap();

            // Update user profile with phone verification status
            await dispatch(UpdateUserProfile({
                id: user.id,
                data: {
                    ...user,
                    phone: phoneNumber.trim(),
                    isPhoneVerified: true
                }
            })).unwrap();

            // Reset OTP state
            setOtp("");
            setIsOtpSent(false);
            setIsOtpVerifying(false);

            GlobalService.showToast("success", t("toast.titles.success"), t("verifyPage.phoneVerifiedSuccess"));
        } catch (error) {
            console.error("Failed to verify OTP:", error);
        } finally {
            setIsOtpVerifying(false);
        }
    };

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, ""); // Only allow digits
        setOtp(value);
    };

    const handleDocumentUpload =
        (type: "identity" | "business" | "addressProof" | any) => async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            setUploadingSection(prev => ({ ...prev, [type]: true }));

            try {
                // Special handling for identity documents (only if not requiring additional documents)
                if (type === "identity") {
                    const requiresAdditionalDocs = verificationData?.identityNotes?.some((note: string) => note.includes("Require Additional Documents"));
                    
                    if (!requiresAdditionalDocs) {
                        const existingIdentityDocs = verificationData?.identityFiles?.length || 0;
                        const alreadyUploadedNew = uploadedFiles.identity.length;
                        const newFilesCount = files.length;
                        const totalAfterUpload = existingIdentityDocs + alreadyUploadedNew + newFilesCount;

                        // Case 1: No existing documents - need exactly 2
                        if (existingIdentityDocs === 0) {
                            if (totalAfterUpload > 2) {
                                GlobalService.showToast("error", t("toast.titles.error"), t("verifyPage.uploadExactly2"));
                                return;
                            }
                        }
                        // Case 2: 1 existing document - need exactly 1 more
                        else if (existingIdentityDocs === 1) {
                            if (totalAfterUpload > 2) {
                                GlobalService.showToast(
                                    "error",
                                    "Error",
                                    t("verifyPage.uploadOnly1More")
                                );
                                return;
                            }
                        }
                        // Case 3: Already has 2+ documents - no more needed
                        else if (existingIdentityDocs >= 2) {
                            GlobalService.showToast(
                                "info",
                                "Info",
                                t("verifyPage.alreadyUploaded")
                            );
                            return;
                        }
                    }
                }

                const uploadedIds: string[] = [];
                const fileNames: string[] = [];

                // Process all files for non-identity, or limited files for identity (unless additional docs required)
                const requiresAdditionalDocs = verificationData?.identityNotes?.some((note: string) => note.includes("Require Additional Documents"));
                
                const filesToProcess =
                    type === "identity" && !requiresAdditionalDocs
                        ? Array.from(files).slice(
                            0,
                            2 - ((verificationData?.identityFiles?.length || 0) + uploadedFiles.identity.length)
                        )
                        : Array.from(files);

                for (const file of filesToProcess) {
                    const { payload } = await dispatch(uploadContractFile(file) as any);
                    const fileId = payload?.file?.id;

                    if (fileId) {
                        uploadedIds.push(fileId);
                        fileNames.push(file.name);
                    }
                }

                setUploadedFiles(prev => ({
                    ...prev,
                    [type]: [...prev[type], ...uploadedIds],
                }));

                setUploadedFileNames(prev => ({
                    ...prev,
                    [type]: [...prev[type], ...fileNames],
                }));

                // Show info message if identity documents are still needed (only if not requiring additional docs)
                if (type === "identity" && !requiresAdditionalDocs) {
                    const totalIdentityDocs =
                        (verificationData?.identityFiles?.length || 0) +
                        uploadedFiles.identity.length +
                        uploadedIds.length;
                    if (totalIdentityDocs === 1) {
                        GlobalService.showToast("info", "Info", "Please upload 1 more identity document");
                    }
                }
            } finally {
                setUploadingSection(prev => ({ ...prev, [type]: false }));
                e.target.value = ""; // Clear the input
            }
        };

    // Handler for additional documents upload
    const handleAdditionalDocumentUpload =
        (type: "identity" | "business" | "addressProof") => async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            setUploadingSection(prev => ({ ...prev, [type]: true }));

            try {
                const uploadedDocs: Array<{ id: string; path: string; status: string }> = [];

                for (const file of Array.from(files)) {
                    const { payload } = await dispatch(uploadContractFile(file) as any);
                    const fileId = payload?.file?.id;
                    const filePath = payload?.file?.path || file.name;

                    if (fileId) {
                        uploadedDocs.push({
                            id: fileId,
                            path: filePath,
                            status: "pending"
                        });
                    }
                }

                setUploadedAdditionalFiles(prev => ({
                    ...prev,
                    [type]: [...prev[type], ...uploadedDocs],
                }));

                GlobalService.showToast("success", t("toast.titles.success"), t("verifyPage.additionalDocsSuccess"));
            } finally {
                setUploadingSection(prev => ({ ...prev, [type]: false }));
                e.target.value = ""; // Clear the input
            }
        };

    const verificationSteps = [
        {
            key: "identity",
            icon: Camera,
            title: t("verifyPage.identityVerification"),
            description: t("verifyPage.identityDescription"),
            status:
                Number(verificationData?.identityStatus?.id) === VerificationStatusEnum.accepted
                    ? "verified"
                    : Number(verificationData?.identityStatus?.id) === VerificationStatusEnum.rejected
                        ? "rejected"
                        : "pending",
            documents: Array.isArray(verificationData?.identityFiles)
                ? verificationData.identityFiles.map((file: any) => ({
                    name: file.path,
                    verified: Number(verificationData?.identityStatus?.id) === VerificationStatusEnum.accepted,
                }))
                : [],
            notes: verificationData?.identityNotes || [],
            verificationType: "identity",
        },
        ...((user?.role.id != RoleEnum.client && company && company.name)
            ? [
                {
                    key: "business",
                    icon: Building,
                    title: t("verifyPage.businessRegistration"),
                    description: t("verifyPage.businessDescription"),
                    status:
                        Number(verificationData?.businessStatus?.id) === VerificationStatusEnum.accepted
                            ? "verified"
                            : Number(verificationData?.businessStatus?.id) === VerificationStatusEnum.rejected
                                ? "rejected"
                                : "pending",
                    documents: Array.isArray(verificationData?.businessFiles)
                        ? verificationData.businessFiles.map((file: any) => ({
                            name: file.path,
                            verified:
                                Number(verificationData?.businessStatus?.id) === VerificationStatusEnum.accepted,
                        }))
                        : [],
                    notes: verificationData?.businessNotes || [],
                    verificationType: "business",
                },
            ]
            : []),
        {
            key: "addressProof",
            icon: MapPin,
            title: t("verifyPage.proofOfAddress"),
            description: t("verifyPage.addressDescription"),
            status:
                Number(verificationData?.addressProofStatus?.id) === VerificationStatusEnum.accepted
                    ? "verified"
                    : Number(verificationData?.addressProofStatus?.id) === VerificationStatusEnum.rejected
                        ? "rejected"
                        : "pending",
            documents: Array.isArray(verificationData?.addressProofFiles)
                ? verificationData.addressProofFiles.map((file: any) => ({
                    name: file.path,
                    verified: Number(verificationData?.addressProofStatus?.id) === VerificationStatusEnum.accepted,
                }))
                : [],
            notes: verificationData?.addressProofNotes || [],
            verificationType: "addressProof",
        },
        {
            key: "email",
            icon: Mail,
            title: t("verifyPage.emailVerification"),
            description: t("verifyPage.emailDescription"),
            status: "verified",
            documents: [],
        },
        // Only show phone verification for professional roles (4, 5, 6)
        ...(Number(user?.role?.id) >= 4 && Number(user?.role?.id) <= 6
            ? [
                {
                    key: "phone",
                    icon: Phone,
                    title: t("verifyPage.phoneVerification"),
                    description: user?.isPhoneVerified
                        ? t("verifyPage.phoneDescriptionVerified", { phone: user.phone })
                        : t("verifyPage.phoneDescription"),
                    status: user?.isPhoneVerified ? "verified" : "pending",
                    documents: [],
                },
            ]
            : []),
    ];
    console.log(verificationSteps);

    const hasNewUploads = Object.values(uploadedFiles).some(fileList => fileList.length > 0) ||
                           Object.values(uploadedAdditionalFiles).some(fileList => fileList.length > 0);
    const validateIdentityDocuments = () => {
        // Skip validation if additional documents are required
        const requiresAdditionalDocs = verificationData?.identityNotes?.some((note: string) => note.includes("Require Additional Documents"));
        if (requiresAdditionalDocs) {
            return true;
        }

        const existingIdentityDocs = verificationData?.identityFiles?.length || 0;
        const newIdentityDocs = uploadedFiles.identity.length;
        const totalIdentityDocs = existingIdentityDocs + newIdentityDocs;

        // Case 1: No existing documents - need at least 2
        if (existingIdentityDocs === 0 && newIdentityDocs < 2) {
            GlobalService.showToast("error", t("toast.titles.error"), t("verifyPage.uploadAtLeast2"));
            return false;
        }

        // Case 2: Some existing documents - need at least 1 more to make total >= 2
        if (existingIdentityDocs > 0 && existingIdentityDocs < 2 && totalIdentityDocs < 2) {
            GlobalService.showToast(
                "error",
                "Error",
                t("verifyPage.uploadAtLeastMore", { count: 2 - existingIdentityDocs })
            );
            return false;
        }

        // Case 3: User uploaded more than 2 new documents when none existed before
        if (existingIdentityDocs === 0 && newIdentityDocs > 2) {
            GlobalService.showToast("error", t("toast.titles.error"), t("verifyPage.max2Documents"));
            return false;
        }

        return true;
    };
    const handleSaveChanges = async () => {
        if (!validateIdentityDocuments()) {
            return;
        }
        const payload = {
            addressProofStatus: { id: VerificationStatusEnum.pending },
            businessStatus: { id: VerificationStatusEnum.pending },
            identityStatus: { id: VerificationStatusEnum.pending },
            addressProofRemarks: "",
            businessRemarks: "",
            identityRemarks: "",
            addressProofFiles: uploadedFiles.addressProof.map(id => ({ id })),
            businessFiles: uploadedFiles.business.map(id => ({ id })),
            identityFiles: uploadedFiles.identity.map(id => ({ id })),
            addressProofNotes: [],
            businessNotes: [],
            identityNotes: [],
            addressProofAdditionalDocuments: uploadedAdditionalFiles.addressProof,
            businessAdditionalDocuments: uploadedAdditionalFiles.business,
            identityAdditionalDocuments: uploadedAdditionalFiles.identity,
            userId: { id: user?.id },
        };

        const { payload: response } = await dispatch(createUserVerificationData({ data: payload }));

        if (response?.id) {
            setIsNewVerificationSubmitted(true);
            await dispatch(currentUserVerificationData({ id: user.id }));
            setUploadedFiles({
                identity: [],
                business: [],
                addressProof: [],
            });
            setUploadedFileNames({
                identity: [],
                business: [],
                addressProof: [],
            });
            setUploadedAdditionalFiles({
                identity: [],
                business: [],
                addressProof: [],
            });
        }
    };

    const handleUpdateChanges = async () => {
        if (!validateIdentityDocuments()) {
            return;
        }
        const getStatus = (statusId: number) => {
            if (
                Number(statusId) === VerificationStatusEnum.pending ||
                Number(statusId) === VerificationStatusEnum.accepted ||
                Number(statusId) === VerificationStatusEnum.rejected
            ) {
                return { id: Number(statusId) };
            }
            return { id: VerificationStatusEnum.pending };
        };

        const mergeFiles = (existing: any[] = [], uploaded: string[] = []) => {
            const uploadedObjects = uploaded.map(id => ({ id }));
            const existingIds = new Set(uploadedObjects.map(file => file.id));
            const preservedExisting = existing.filter(file => !existingIds.has(file.id));
            return [...preservedExisting, ...uploadedObjects];
        };

        const mergeAdditionalDocs = (existing: any[] = [], uploaded: Array<{ id: string; path: string; status: string }> = []) => {
            return [...(existing || []), ...uploaded];
        };

        // Remove "Require Additional Documents" notes from types that have new uploads
        const removeAdditionalDocNotes = (notes: string[] = [], hasNewUploads: boolean) => {
            if (hasNewUploads) {
                return notes.filter(note => !note.includes("Require Additional Documents"));
            }
            return notes;
        };

        const hasAdditionalUploads = uploadedAdditionalFiles.identity.length > 0 || 
                                      uploadedAdditionalFiles.business.length > 0 || 
                                      uploadedAdditionalFiles.addressProof.length > 0;

        const payload = {
            addressProofStatus: getStatus(verificationData?.addressProofStatus?.id),
            businessStatus: getStatus(verificationData?.businessStatus?.id),
            identityStatus: getStatus(verificationData?.identityStatus?.id),
            addressProofRemarks: verificationData?.addressProofRemarks,
            businessRemarks: verificationData?.businessRemarks,
            identityRemarks: verificationData?.identityRemarks,
            addressProofFiles: mergeFiles(verificationData?.addressProofFiles, uploadedFiles.addressProof),
            businessFiles: mergeFiles(verificationData?.businessFiles, uploadedFiles.business),
            identityFiles: mergeFiles(verificationData?.identityFiles, uploadedFiles.identity),
            addressProofNotes: removeAdditionalDocNotes(verificationData?.addressProofNotes, uploadedAdditionalFiles.addressProof.length > 0),
            businessNotes: removeAdditionalDocNotes(verificationData?.businessNotes, uploadedAdditionalFiles.business.length > 0),
            identityNotes: removeAdditionalDocNotes(verificationData?.identityNotes, uploadedAdditionalFiles.identity.length > 0),
            addressProofAdditionalDocuments: mergeAdditionalDocs(verificationData?.addressProofAdditionalDocuments, uploadedAdditionalFiles.addressProof),
            businessAdditionalDocuments: mergeAdditionalDocs(verificationData?.businessAdditionalDocuments, uploadedAdditionalFiles.business),
            identityAdditionalDocuments: mergeAdditionalDocs(verificationData?.identityAdditionalDocuments, uploadedAdditionalFiles.identity),
            userId: { id: user?.id },
        };
        await dispatch(updateUserVerification({ id: verificationData?.id, data: payload }));
        await dispatch(currentUserVerificationData({ id: user.id }));
        setUploadedFiles({
            identity: [],
            business: [],
            addressProof: [],
        });
        setUploadedFileNames({
            identity: [],
            business: [],
            addressProof: [],
        });
        setUploadedAdditionalFiles({
            identity: [],
            business: [],
            addressProof: [],
        });
    };


    return (
        <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
            <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
                {isProfessional ? <SharedHeader4 /> : <SharedHeader5 />}
            </div>
            <div className="min-h-[calc(100vh-64px)] pt-16">
                <main className="p-4 lg:p-8 overflow-y-auto h-[calc(100vh-64px)] w-full">
                    <div className="max-w-3xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center justify-center p-2 bg-blue-100 rounded-full mt-2 mb-4">
                                    <Shield className="h-8 w-8 text-blue-600" />
                                </div>
                                <h1 className="text-3xl font-bold text-gray-900">{t("verifyPage.title")}</h1>
                                <p className="mt-2 text-gray-600">
                                    {t("verifyPage.subtitle")}
                                </p>

                                {/* Account Type Chip */}
                                <div className="mt-6 flex justify-center">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-200">
                                        <User className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-medium text-blue-700">
                                            {isProfessional ? t("verifyPage.professionalAccount") : t("verifyPage.clientAccount")}
                                        </span>
                                    </div>
                                </div>

                                {/* Verification Requirements Info */}
                                <div className="mt-8 p-6 rounded-lg shadow-sm text-left border bg-blue-50">
                                    <div className="flex items-start gap-2 mb-3">
                                        <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                {isProfessional ? t("verifyPage.professionalRequirementsTitle") : t("verifyPage.clientRequirementsTitle")}
                                            </h3>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                {isProfessional
                                                    ? t("verifyPage.professionalRequirementsDesc")
                                                    : t("verifyPage.clientRequirementsDesc")
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-blue-500 rounded-full"
                                                    style={{ width: `${calculateVerificationProgress()}%` }}
                                                />
                                            </div>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">
                                            {calculateVerificationProgress()}% {t("verifyPage.complete")}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {verificationSteps.some(step => step.status === "rejected") && (
                                <Alert className="mb-8 bg-red-50 text-red-800 border-red-200 flex items-center">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription className="ml-2">
                                        One or more documents were rejected. Please review the issues and resubmit.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-4">
                                {verificationSteps.map((step, index) => {
                                    // Combine backend additional documents with local uploads
                                    const getAdditionalDocuments = (key: string) => {
                                        const backendDocs = (() => {
                                            switch (key) {
                                                case "identity":
                                                    return verificationData?.identityAdditionalDocuments || [];
                                                case "business":
                                                    return verificationData?.businessAdditionalDocuments || [];
                                                case "addressProof":
                                                    return verificationData?.addressProofAdditionalDocuments || [];
                                                default:
                                                    return [];
                                            }
                                        })();
                                        const localDocs = uploadedAdditionalFiles[key] || [];
                                        return [...backendDocs, ...localDocs];
                                    };

                                    const getAdditionalFileHandler = (key: string) => {
                                        if (key === "identity" || key === "business" || key === "addressProof") {
                                            return handleAdditionalDocumentUpload(key);
                                        }
                                        return () => {};
                                    };

                                    return (
                                        <VerificationStep
                                            key={index}
                                            {...step}
                                            onUpload={() => document.getElementById(`file-input-${index}`)?.click()}
                                            inputId={`file-input-${index}`}
                                            isIdentity={step.key === "identity"}
                                            isPhone={step.key === "phone"}
                                            onFileSelect={handleDocumentUpload(step.key)}
                                            uploadedFileName={uploadedFileNames[step.key]}
                                            isUploaded={uploadedFiles[step.key]?.length > 0}
                                            isUploading={!!uploadingSection[step.key]}
                                            phoneNumber={phoneNumber}
                                            onPhoneNumberChange={handlePhoneNumberChange}
                                            onSendVerificationCode={handleSendVerificationCode}
                                            otp={otp}
                                            onOtpChange={handleOtpChange}
                                            onVerifyOtp={handleVerifyOtp}
                                            isOtpSent={isOtpSent}
                                            isOtpVerifying={isOtpVerifying}
                                            user={user}
                                            notes={step.notes}
                                            verificationType={step.verificationType}
                                            hasVerificationData={!!verificationData?.id}
                                            additionalDocuments={getAdditionalDocuments(step.key)}
                                            onAdditionalFileSelect={getAdditionalFileHandler(step.key)}
                                        />
                                    );
                                })}
                            </div>

                            <div className="mt-8 flex justify-end">
                                <Button
                                    onClick={() => {
                                        if (!verificationData?.id && hasNewUploads) {
                                            handleSaveChanges();
                                        } else if (verificationData?.id && hasNewUploads) {
                                            handleUpdateChanges();
                                        }
                                    }}
                                    disabled={!hasNewUploads}>
                                    {isLoading ? (
                                        <>
                                            <Spinner size={5} />
                                            {t("verifyPage.loading")}
                                        </>
                                    ) : verificationData?.id ? (
                                        t("verifyPage.updateChanges")
                                    ) : (
                                        t("verifyPage.saveChanges")
                                    )}
                                </Button>
                            </div>

                            {/* Verification Complete Card */}
                            {calculateVerificationProgress() === 100 && (
                                <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <Check className="h-6 w-6 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-green-800 mb-2">
                                                {t("verifyPage.verificationComplete")}
                                            </h3>
                                            <p className="text-black-700">
                                                {t("verifyPage.maintenanceMessage")}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {calculateVerificationProgress() < 100 && <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border">
                                <h3 className="font-medium text-gray-900 mb-4">{t("verifyPage.whatHappensNext")}</h3>
                                <div className="space-y-4">
                                    {[1, 2, 3].map(step => (
                                        <div className="flex items-start gap-3" key={step}>
                                            <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                                                {step}
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">
                                                    {step === 1 &&
                                                        t("verifyPage.step1")}
                                                    {step === 2 &&
                                                        t("verifyPage.step2")}
                                                    {step === 3 &&
                                                        (isProfessional
                                                            ? t("verifyPage.step3Professional")
                                                            : t("verifyPage.step3Client")
                                                        )}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>}

                            <div className="mt-8 text-center">
                                <p className="text-sm text-gray-600">
                                    {t("verifyPage.needHelp")}{" "}
                                    <Link to="/menu/contact" className="text-blue-600 hover:text-blue-700 font-medium">
                                        {t("verifyPage.contactSupport")} <ChevronRight className="inline h-4 w-4" />
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </main>
                    <footer className="bg-white border-t mt-auto w-full">
                        <div className="max-w-7xl mx-auto">
                            <SharedFooter3 />
                        </div>
                    </footer>
            </div>
        </div>
    );
};

export default VerifyAccount;
